import { watch, type Component } from 'vue';

// PRODUCT-26110 Phase 2B2: 'vue-router/types/router' is a vue-router-3-only subpath,
// not present in v4's `exports` map at all -- module-not-found, not just a type
// mismatch. `RawLocation` -> `RouteLocationRaw`, `Route` -> `RouteLocationNormalized`,
// `RouteConfig` -> `RouteRecordRaw`.
import noop from 'lodash/noop';
import { RouteLocationRaw, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { IamClient } from '@evidentid/iam-client';
import type Logger from '@evidentid/logger';
import Severity from '@evidentid/logger/Severity';
import RpWebApiClient, { RelyingPartiesQuery } from '@evidentid/rpweb-api-client';
import type Tracker from '@evidentid/tracker';
import withPropsInjected from '@evidentid/vue-commons/decorators/withPropsInjected';

import { createModuleBuilder } from '../../app';
import { PersistingErrorModule } from '../persisting-error';
import { usePersistingErrorStore } from '../persisting-error/store';
import { SnackbarModule } from '../snackbar';

import AuthLoginWrapper from './components/AuthLoginWrapper.vue';
import AuthTermsWrapper from './components/AuthTermsWrapper.vue';
import { createRpController } from './createRpController';
import { getAuthRedirection } from './getAuthRedirection';
import { useTermsStore } from './store/terms';
import { useUserStore } from './store/user';
import { UserState, RelyingParty } from './types';

export interface AuthModuleExpectedViews {
    Loading: Component;
    Login: Component;
    VerifyEmail: Component;
    ResetPassword: Component;
    Register: Component;
    Terms: Component;
}

export interface RpController {
    current: string | null;
    available: RelyingParty[];
    change(rpId: string): Promise<any>;
}

function saveLastRpName(name: string): void {
    try {
        localStorage.setItem('$lastSelectedRp', name);
    } catch (error) {
        // localStorage is not supported, so just ignore it
    }
}

type DefaultRoute = ((to: RouteLocationNormalized) => RouteLocationRaw) | RouteLocationRaw;

export function createAuthModule() {
    return createModuleBuilder()
        .demandOptions<{
        terms: { content: string, hash: string };
        defaultAuthenticatedRoute?: DefaultRoute;
        relyingPartiesFilter: RelyingPartiesQuery;
        views: AuthModuleExpectedViews;
    }>()
        .demandContext<{
        rpweb: RpWebApiClient;
        gatewayClient: GatewayApiClient;
        auth: IamClient;
        authType: string;
        tracker?: Tracker<any>;
        logger?: Logger<any>;
    }>()
        .demand<SnackbarModule>()
        .demand<PersistingErrorModule>()
        .injectVue((app) => ({
            $rp: createRpController(app),
            $auth: {
                get type() {
                    return app.authType;
                },
                get availableLoginMethods() {
                    return app.auth.getAvailableLoginMethods();
                },
                get availableRegisterMethods() {
                    return app.auth.getAvailableRegisterMethods();
                },
                get canRegister() {
                    return app.auth.getAvailableRegisterMethods().length > 0;
                },
                get canResetPassword() {
                    return app.auth.canResetPassword;
                },
                get canSendVerificationEmail() {
                    return app.auth.canSendVerificationEmail;
                },
                get client() {
                    return app.auth;
                },
            },
        }))
        .execute((app) => {
            const userStore = useUserStore(app.getPinia());
            app.auth.subscribe((data) => {
                userStore.handleUserDataUpdate(data).catch(noop);
            });
        })
        .execute((app) => {
            const userStore = useUserStore(app.getPinia());
            // Clear errors after logout
            watch(
                () => userStore.data,
                (userData) => {
                    // PRODUCT-26110 Phase 2B2: router.currentRoute is a Ref under
                    // vue-router 4.
                    const route = app.router.currentRoute.value;
                    const errorStore = usePersistingErrorStore(app.getPinia());
                    if (!userData && errorStore.error) {
                        errorStore.showError(null);
                    } else if (!userData && route.name === 'error' && route.params.reason === 'no-relying-parties') {
                        app.router.replace({ name: 'auth.login', query: { force: '' } }).catch(noop);
                    }
                },
            );
        })
        .execute((app) => {
            app.router.beforeEach((route, _, next) => {
                // Extract details
                const user = useUserStore(app.getPinia());
                const defaultRoute = app.options.defaultAuthenticatedRoute;
                const getDefaultAuthenticatedRoute = typeof defaultRoute === 'function'
                    ? defaultRoute
                    : () => (defaultRoute || '/') as RouteLocationRaw;
                // vue-router 4 types repeated params as `string | string[]`, stricter
                // than vue-router 3 -- this route has no repeated `rpId` segment, so
                // `as string` matches ed2202df8's established narrowing.
                const sign = useTermsStore(app.getPinia()).signDetails[route.params.rpId as string];

                // Analyze data
                const destination = getAuthRedirection({ user, route, sign, getDefaultAuthenticatedRoute });

                // Redirect when expected
                if (destination !== null) {
                    return next(destination.to);
                }

                // Save RP ID when it is available
                if (route.params.rpId) {
                    saveLastRpName(route.params.rpId as string);
                }

                // Accept navigation
                return next();
            });
        })
        .execute((app) => {
            function tick() {
                // Extract details
                // PRODUCT-26110 Phase 2B2: router.currentRoute is a Ref under
                // vue-router 4.
                const route = app.router.currentRoute.value;
                const user = useUserStore(app.getPinia());
                const defaultRoute = app.options.defaultAuthenticatedRoute;
                const getDefaultAuthenticatedRoute = typeof defaultRoute === 'function'
                    ? defaultRoute
                    : () => (defaultRoute || '/') as RouteLocationRaw;
                // vue-router 4 types repeated params as `string | string[]`, stricter
                // than vue-router 3 -- this route has no repeated `rpId` segment, so
                // `as string` matches ed2202df8's established narrowing.
                const sign = useTermsStore(app.getPinia()).signDetails[route.params.rpId as string];
                // Analyze data
                const destination = getAuthRedirection({ user, route, sign, getDefaultAuthenticatedRoute });

                // Redirect when expected
                if (destination !== null) {
                    if (destination.replace) {
                        app.router.replace(destination.to).catch(noop);
                    } else {
                        app.router.push(destination.to).catch(noop);
                    }
                }
            }
            const userStore = useUserStore(app.getPinia());
            const termsStore = useTermsStore(app.getPinia());
            watch(() => userStore.initialized, tick);
            watch(() => userStore.data, tick);
            watch(() => userStore.relyingParties, tick);
            watch(() => termsStore.signDetails, tick);
        })
        .registerRoutes((app) => [
            {
                path: '/auth',
                name: 'auth.login',
                component: withPropsInjected(AuthLoginWrapper, {
                    Component: app.options.views.Login,
                    Loading: app.options.views.Loading,
                }),
                meta: {
                    title: 'Log in',
                    guestOnly: true,
                },
            },
            {
                path: '/forgot-password',
                name: 'auth.resetPassword',
                component: app.options.views.ResetPassword,
                meta: {
                    title: 'Reset Password',
                    guestOnly: true,
                },
                beforeEnter: (to, from, next) => {
                    if (app.auth.canResetPassword) {
                        next();
                    } else {
                        next({ name: 'auth.login' });
                    }
                },
            },
            {
                path: '/register',
                name: 'auth.register',
                component: app.options.views.Register,
                meta: {
                    title: 'Register Account',
                    guestOnly: true,
                },
                beforeEnter: (to, from, next) => {
                    if (app.auth.getAvailableRegisterMethods().length > 0) {
                        next();
                    } else {
                        next({ name: 'auth.login' });
                    }
                },
            },
            {
                path: '/auth/check',
                name: 'auth.check',
                component: app.options.views.Loading,
            },
            {
                path: '/auth/verify',
                name: 'auth.verify',
                // TODO: Disallow re-sending verification e-mail for Auth0
                component: app.options.views.VerifyEmail,
                meta: {
                    title: 'Verify Account',
                },
            },
            {
                path: '/auth/terms/:rpId?',
                name: 'auth.terms',
                component: withPropsInjected(AuthTermsWrapper, {
                    Component: app.options.views.Terms,
                    Loading: app.options.views.Loading,
                    content: app.options.terms.content,
                    hash: app.options.terms.hash,
                }),
                meta: {
                    title: 'Terms and Conditions',
                },
            },
            {
                path: '/auth/terms/:rpId?/redirect',
                name: 'auth.terms.redirect',
                component: withPropsInjected(AuthTermsWrapper, {
                    Component: app.options.views.Terms,
                    Loading: app.options.views.Loading,
                    content: app.options.terms.content,
                    hash: app.options.terms.hash,
                }),
                meta: {
                    title: 'Terms and Conditions',
                },
            },
            {
                path: '/auth/error/no-relying-parties',
                name: 'auth.noRelyingParties',
                redirect: (to) => ({
                    name: 'error',
                    params: { reason: 'no-relying-parties' },
                    query: { next: to.query.next },
                }),
            },
        ] as RouteRecordRaw[])

        // Track "log in" events
        .execute((app) => {
            function onLogInRequest(provider: string) {
                if (app.tracker) {
                    app.tracker.track({
                        name: 'Login requested',
                        description: `Login requested by ${provider} provider`,
                        provider,
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        category: 'user',
                        message: '[Requested] Log in',
                        data: { provider },
                    });
                }
            }

            function onLogInFinish(provider: string) {
                if (app.tracker) {
                    app.tracker.track({
                        name: 'Login finished',
                        description: `Login finished by ${provider} provider`,
                        provider,
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        category: 'user',
                        message: '[Finished] Log in',
                        data: { provider },
                    });
                }
            }

            function onLogInFailure(error: any) {
                if (app.tracker) {
                    app.tracker.track({
                        name: 'Login failed',
                        description: 'Login failed',
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        level: Severity.warning,
                        category: 'user',
                        message: '[Failed] Log in',
                        data: { error },
                    });
                }
            }

            useUserStore(app.getPinia()).$onAction(({ name, args, after }) => {
                if (name === 'logInWithSso') {
                    onLogInRequest(args[0] as string);
                    after(() => onLogInFinish(args[0] as string));
                } else if (name === 'logIn') {
                    onLogInRequest('regular');
                    after(() => onLogInFinish('regular'));
                } else if (name === 'showLoginError') {
                    onLogInFailure(args[0]);
                }
            });
        })

        // Track "reset password" events
        .execute((app) => {
            function onResetPasswordRequest() {
                if (app.tracker) {
                    app.tracker.track({
                        name: 'Reset password requested',
                        description: 'Reset password requested',
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        category: 'user',
                        message: '[Requested] Reset password',
                    });
                }
            }

            function onResetPasswordFinish() {
                if (app.tracker) {
                    app.tracker.track({
                        name: 'Reset password finished',
                        description: 'Reset password finished',
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        category: 'user',
                        message: '[Finished] Reset password',
                    });
                }
            }

            function onResetPasswordFailure(error: any) {
                if (app.tracker) {
                    app.tracker.track({
                        name: 'Reset password failed',
                        description: 'Reset password failed',
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        level: Severity.warning,
                        category: 'user',
                        message: '[Failed] Reset password',
                        data: { error },
                    });
                }
            }

            useUserStore(app.getPinia()).$onAction(({ name, args, after }) => {
                if (name === 'resetPassword') {
                    onResetPasswordRequest();
                    after(onResetPasswordFinish);
                } else if (name === 'showResetError') {
                    onResetPasswordFailure(args[0]);
                }
            });
        })

        // Track "log out" events
        .execute((app) => {
            function onLogOutRequest() {
                if (app.tracker) {
                    app.tracker.track({
                        name: 'Logout requested',
                        description: 'Logout requested',
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        category: 'user',
                        message: '[Requested] Log out',
                    });
                }
            }

            function onLogOutFinish() {
                if (app.tracker) {
                    app.tracker.track({
                        name: 'Logout finished',
                        description: 'Logout finished',
                    });
                }

                if (app.logger) {
                    app.logger.addBreadcrumb({
                        category: 'user',
                        message: '[Finished] Log out',
                    });
                }
            }

            useUserStore(app.getPinia()).$onAction(({ name, after }) => {
                if (name === 'logOut') {
                    onLogOutRequest();
                    after(onLogOutFinish);
                }
            });
        })

        // Track change events
        .execute((app) => {
            function onUserChange(user: UserState['data']) {
                if (app.tracker) {
                    if (user?.email) {
                        app.tracker.appendUserData({
                            id: user.email,
                            name: user.displayName || user.email,
                            email: user.email,
                            authType: app.authType,
                        });
                    } else {
                        app.tracker.setUserData(null);
                    }
                }

                if (app.logger) {
                    if (user?.email) {
                        app.logger.appendUserData({
                            id: user.email,
                            name: user.displayName || user.email,
                            email: user.email,
                            authType: app.authType,
                        });
                        app.logger.addBreadcrumb({
                            category: 'user',
                            message: 'Loaded user data',
                        });
                    } else {
                        app.logger.setUser(null);
                        app.logger.addBreadcrumb({
                            category: 'user',
                            message: 'Cleared user data',
                        });
                    }
                }
            }

            function onRelyingPartiesChange(rps: RelyingParty[] | null) {
                if (app.tracker) {
                    if (rps) {
                        app.tracker.appendUserData({ rpsCount: rps.length });
                        app.tracker.track({
                            name: 'List of available RPs loaded',
                            description: `List of available RPs loaded`,
                        });
                    } else {
                        app.tracker.track({
                            name: 'List of available RPs cleared',
                            description: 'List of available RPs cleared',
                        });
                    }
                }

                if (app.logger) {
                    if (rps) {
                        app.logger.addBreadcrumb({
                            category: 'user',
                            message: 'Loaded available RPs list',
                            data: { count: rps.length },
                        });
                    } else {
                        app.logger.addBreadcrumb({
                            category: 'user',
                            message: 'Cleared list of available RPs',
                        });
                    }
                }
            }

            function onSelectedRpChange(rp: string | null) {
                if (app.tracker) {
                    app.tracker.appendUserData({ currentRp: rp || '<none>' });
                }

                if (app.logger) {
                    app.logger.setTag('rp', rp || '<none>');
                    app.logger.addBreadcrumb({
                        category: 'route',
                        message: 'Changed selected RP',
                        data: { rpName: rp || '<none>' },
                    });
                }
            }

            const userStore = useUserStore(app.getPinia());
            watch(() => userStore.data, onUserChange);
            watch(() => userStore.relyingParties, onRelyingPartiesChange);
            app.observer.onRouteChange((route, prevRoute) => {
                const prevRpId = prevRoute?.params?.rpId;
                const rpId = route?.params?.rpId;
                if (prevRpId !== rpId) {
                    // vue-router 4 types repeated params as `string | string[]`,
                    // stricter than vue-router 3 -- this route has no repeated `rpId`
                    // segment, so `as string | null` matches ed2202df8's established
                    // narrowing.
                    onSelectedRpChange(rpId as string | null);
                }
            });
        })

        .end();
}

export type AuthModule = ReturnType<typeof createAuthModule>;
